import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Minus, Plus } from "lucide-react";
import ToolShell, { Panel, FieldLabel, Choice } from "@/components/tools/ToolShell";
import { SETUP_CONFIG, TOOL_PATHS, calcSetup, formatEUR } from "@/config/tools";

const range = ([a, b], lang) => (a === b ? formatEUR(a, lang) : `${formatEUR(a, lang)} – ${formatEUR(b, lang)}`);

export default function SetupEstimator() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [type, setType] = useState("trading");
  const [shareholders, setShareholders] = useState(1);
  const [extras, setExtras] = useState(["bank"]);
  const r = calcSetup({ type, shareholders, extras });
  const toggle = (e) => setExtras((xs) => (xs.includes(e) ? xs.filter((x) => x !== e) : [...xs, e]));

  return (
    <ToolShell id="setup" path={TOOL_PATHS.setup}>
      <div className="grid gap-px bg-[var(--line)] lg:grid-cols-12">
        <Panel className="lg:col-span-7 bg-[var(--ink)] border-0 space-y-10">
          <div>
            <FieldLabel>{t("tools.setup.type")}</FieldLabel>
            <div className="grid gap-3 sm:grid-cols-3">
              {Object.keys(SETUP_CONFIG.types).map((k) => (
                <Choice key={k} active={type === k} onClick={() => setType(k)} testid={`setup-type-${k}`}>
                  <div className="text-[var(--bone)]">{t(`tools.setup.types.${k}.label`)}</div>
                  <div className="text-xs text-[var(--mute)] mt-1">{t(`tools.setup.types.${k}.desc`)}</div>
                </Choice>
              ))}
            </div>
          </div>
          <div>
            <FieldLabel>{t("tools.setup.shareholders")}</FieldLabel>
            <div className="inline-flex items-center border border-[var(--line-2)]">
              <button type="button" className="p-3 text-[var(--bone-2)] hover:text-[var(--pewter)]" onClick={() => setShareholders((n) => Math.max(1, n - 1))} data-testid="setup-shareholders-minus" aria-label="-">
                <Minus size={14} />
              </button>
              <span className="w-12 text-center font-mono text-[var(--bone)]" data-testid="setup-shareholders-value">{shareholders}</span>
              <button type="button" className="p-3 text-[var(--bone-2)] hover:text-[var(--pewter)]" onClick={() => setShareholders((n) => Math.min(SETUP_CONFIG.maxShareholders, n + 1))} data-testid="setup-shareholders-plus" aria-label="+">
                <Plus size={14} />
              </button>
            </div>
          </div>
          <div>
            <FieldLabel>{t("tools.setup.extras")}</FieldLabel>
            <div className="grid gap-3 sm:grid-cols-2">
              {Object.keys(SETUP_CONFIG.extras).map((e) => (
                <Choice key={e} active={extras.includes(e)} onClick={() => toggle(e)} testid={`setup-extra-${e}`}>
                  <span className="flex items-center gap-3">
                    <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${extras.includes(e) ? "border-[var(--pewter)] bg-[var(--pewter)] text-[var(--ink)]" : "border-[var(--line-2)]"}`}>
                      {extras.includes(e) && <Check size={11} />}
                    </span>
                    <span className="flex-1">{t(`tools.setup.extraItems.${e}`)}</span>
                    {SETUP_CONFIG.extras[e].quote && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--mute)]">{t("tools.setup.quote")}</span>
                    )}
                  </span>
                </Choice>
              ))}
            </div>
          </div>
        </Panel>
        <Panel className="lg:col-span-5 bg-[var(--ink-2)] border-0 flex flex-col gap-8" data-testid="setup-result">
          {[
            ["setupCost", range(r.setup, lang), "setup-result-setup"],
            ["annualCost", range(r.annual, lang), "setup-result-annual"],
            ["timeline", t("tools.setup.weeks", { min: r.weeks[0], max: r.weeks[1] }), "setup-result-weeks"],
          ].map(([k, v, id]) => (
            <div key={k} className="border-b border-[var(--line)] pb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--pewter)]">{t(`tools.setup.${k}`)}</div>
              <div className="display text-3xl md:text-4xl text-[var(--bone)] mt-2" dir="ltr" style={{ textAlign: "start" }} data-testid={id}>{v}</div>
            </div>
          ))}
          {r.quoted.length > 0 && (
            <p className="text-sm text-[var(--bone-2)] leading-relaxed ps-4 border-s-2 border-[var(--pewter)]/60" data-testid="setup-result-quoted">
              {t("tools.setup.quoteNote", { items: r.quoted.map((q) => (q === "shareholders" ? t("tools.setup.extraShareholders") : t(`tools.setup.extraItems.${q}`))).join(", ") })}
            </p>
          )}
          <p className="text-sm text-[var(--mute)] leading-relaxed">{t("tools.setup.includes")}</p>
        </Panel>
      </div>
    </ToolShell>
  );
}
